/**
 * HYPSEYA — Guide
 * A map with doors, not a manual: "your first day" actions up top,
 * a small visual per chapter, and every chapter ending in the app itself.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const TEAL = '#1E6B68';
const TEAL_LIGHT = '#2D8B87';

// ── Chapter mini-visuals — the app's own visual language, miniaturised ──────

function LoopVisual() {
  const steps = ['Orient', 'Practice', 'Observe', 'Integrate'];
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '6px 0 18px' }}>
      {steps.map((s, i) => (
        <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: 34, height: 34, borderRadius: '50%', margin: '0 auto 4px',
              background: i === 0 ? `linear-gradient(135deg, ${TEAL}, ${TEAL_LIGHT})` : 'rgba(184,211,207,0.3)',
              color: i === 0 ? '#fff' : '#5A7A77',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.8rem', fontWeight: 700,
            }}>{i + 1}</div>
            <span style={{ fontSize: '0.6rem', color: '#8FA3A0', letterSpacing: '0.03em' }}>{s}</span>
          </div>
          {i < 3 && <span style={{ color: '#B8D3CF', fontSize: '0.8rem', marginBottom: 14 }}>→</span>}
        </div>
      ))}
    </div>
  );
}

function EngineVisual() {
  const rows = [
    { name: 'Physiological Sigh', w: 84 },
    { name: '4-7-8 Breath', w: 58 },
    { name: 'Body Scan', w: 36 },
  ];
  return (
    <div style={{ padding: '2px 8px 18px', maxWidth: 320, margin: '0 auto' }}>
      {rows.map(r => (
        <div key={r.name} style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.62rem', color: '#8FA3A0', marginBottom: 3 }}>
            <span>{r.name}</span><span style={{ color: TEAL, fontWeight: 700 }}>−{(r.w / 28).toFixed(1)}</span>
          </div>
          <div style={{ height: 5, background: 'rgba(184,211,207,0.25)', borderRadius: 3 }}>
            <div style={{ height: '100%', width: `${r.w}%`, borderRadius: 3, background: `linear-gradient(90deg, ${TEAL}, ${TEAL_LIGHT})` }} />
          </div>
        </div>
      ))}
      <p style={{ margin: '6px 0 0', fontSize: '0.6rem', color: '#B8C9C6', textAlign: 'center', fontStyle: 'italic' }}>
        Illustrative — your ranking is built from your own sessions.
      </p>
    </div>
  );
}

function VoiceVisual() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3, padding: '10px 0 20px' }}>
      {[10, 18, 28, 40, 30, 44, 24, 36, 46, 32, 20, 38, 26, 16, 10].map((h, i) => (
        <div key={i} style={{
          width: 4, height: h, borderRadius: 2,
          background: i % 3 === 0 ? TEAL : 'rgba(45,139,135,0.45)',
        }} />
      ))}
    </div>
  );
}

function ERIVisual() {
  const dims = [
    { label: 'Regulation', w: 72 },
    { label: 'Stability', w: 58 },
    { label: 'Consistency', w: 80 },
  ];
  return (
    <div style={{ display: 'flex', justifyContent: 'center', gap: 18, padding: '4px 0 18px' }}>
      {dims.map(d => (
        <div key={d.label} style={{ textAlign: 'center' }}>
          <div style={{
            width: 52, height: 52, borderRadius: '50%', margin: '0 auto 5px',
            background: `conic-gradient(${TEAL} ${d.w}%, rgba(184,211,207,0.25) 0)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{
              width: 40, height: 40, borderRadius: '50%', background: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.72rem', fontWeight: 700, color: TEAL,
            }}>{d.w}</div>
          </div>
          <span style={{ fontSize: '0.6rem', color: '#8FA3A0' }}>{d.label}</span>
        </div>
      ))}
    </div>
  );
}

function JournalVisual() {
  return (
    <div style={{ maxWidth: 320, margin: '0 auto', padding: '2px 8px 18px' }}>
      <div style={{
        background: 'rgba(30,107,104,0.06)', borderRadius: '12px 12px 12px 3px',
        padding: '8px 12px', fontSize: '0.68rem', color: '#4A6B68', marginBottom: 8, fontStyle: 'italic',
      }}>
        Where did the breath show up in your day — even in a small way?
      </div>
      <div style={{
        background: '#fff', border: '1px solid rgba(184,211,207,0.6)',
        borderRadius: '12px 12px 3px 12px', padding: '8px 12px',
        fontSize: '0.68rem', color: '#5A7A77', marginBottom: 8, marginLeft: 24,
      }}>
        Chest loosened on the walk. I keep noticing movement changes everything…
      </div>
      <div style={{
        background: 'rgba(30,107,104,0.06)', borderRadius: '12px 12px 12px 3px',
        padding: '8px 12px', fontSize: '0.68rem', color: '#4A6B68', fontStyle: 'italic',
      }}>
        Movement keeps appearing as the thing that shifts you.
      </div>
    </div>
  );
}

function ProgramVisual() {
  return (
    <div style={{ maxWidth: 340, margin: '0 auto', padding: '10px 8px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {[1, 2, 3, 4, 5, 6, 7, 8].map(w => (
          <div key={w} style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
            <div style={{
              width: w === 1 || w === 8 ? 22 : 12, height: w === 1 || w === 8 ? 22 : 12,
              borderRadius: '50%', flexShrink: 0,
              background: w === 1 ? TEAL : w === 8 ? '#B8906A' : 'rgba(184,211,207,0.45)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: '0.55rem', fontWeight: 700,
            }}>{w === 1 ? 'W1' : w === 8 ? 'W8' : ''}</div>
            {w < 8 && <div style={{ flex: 1, height: 2, background: 'rgba(184,211,207,0.4)' }} />}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
        <span style={{ fontSize: '0.6rem', color: TEAL, fontWeight: 600 }}>Baseline measured</span>
        <span style={{ fontSize: '0.6rem', color: '#B8906A', fontWeight: 600 }}>Re-test · your change</span>
      </div>
    </div>
  );
}

// ── First-day actions ────────────────────────────────────────────────────────

const FIRST_DAY = [
  {
    n: 1,
    title: 'Do your first Daily Loop',
    text: 'Seven minutes. Orient, practise, observe, integrate. This is the heartbeat of everything else.',
    path: '/',
  },
  {
    n: 2,
    title: 'Create a meditation — and hear a sample',
    text: 'Describe what you need, pick a voice, listen to 30 seconds before the full audio.',
    path: '/studio',
  },
  {
    n: 3,
    title: 'Write — or speak — your first Journal entry',
    text: 'The Journal asks you a question about your day. Answer it however it comes.',
    path: '/journal',
  },
];

// ── Chapters ─────────────────────────────────────────────────────────────────

const CHAPTERS = [
  {
    id: 'daily-loop',
    title: 'The Daily Loop',
    subtitle: 'Orient → Practice → Observe → Integrate',
    Visual: LoopVisual,
    cta: { label: 'Start today\'s loop →', path: '/' },
    content: [
      {
        step: 'Orient',
        text: 'Each session begins with orientation — a short piece of content that frames the emotional territory you\'ll be working in. If you\'re on a program, your week\'s theme frames the day. It sets the nervous system\'s direction before the work begins.',
      },
      {
        step: 'Practice',
        text: 'The core of the session. A guided practice — breathwork, somatic, or grounding — recommended for you specifically (see "The app learns you" below). Shorter is not easier. Depth is not measured in time.',
      },
      {
        step: 'Observe',
        text: 'A three-point check-in: activation, presence, and openness. These become your ERI data points — and they teach the engine what actually works on you. Naming a state changes your relationship to it.',
      },
      {
        step: 'Integrate',
        text: 'A brief reflection that anchors what moved. Not journaling — more like a signal to the nervous system that the loop is closing. Completion matters. Incompletion costs more than people realise.',
      },
    ],
    footer: 'The core of the practice. Every day, same loop. Different depth each time.',
  },
  {
    id: 'engine',
    title: 'The app learns you',
    subtitle: 'Your personal regulation ranking',
    Visual: EngineVisual,
    cta: { label: 'See what regulates you →', path: '/insights' },
    content: [
      {
        step: 'Every practice is measured',
        text: 'You arrive with an activation level. You practise. You check out. The difference — your regulation delta — is recorded, session after session.',
      },
      {
        step: 'Your personal ranking',
        text: 'Across sessions, HYPSEYA learns which practices actually move your system — not what works on average, but what works on you. Box breathing may calm your friend and do nothing for you. The engine notices.',
      },
      {
        step: 'The recommendation',
        text: 'Each day, the Practice step suggests what your data says will regulate you — and tells you why. Arrive highly activated, and it reaches for your fastest down-regulator.',
      },
    ],
    footer: 'Six practices. One nervous system — yours. The ranking is yours alone.',
  },
  {
    id: 'voice',
    title: 'Your voice',
    subtitle: 'Meditations spoken by you, for you',
    Visual: VoiceVisual,
    cta: { label: 'Create your voice →', path: '/studio' },
    content: [
      {
        step: 'Record once',
        text: 'Four short readings in the Voice Lab. That\'s all it takes to create your voice — private, encrypted, used only for your own meditations.',
      },
      {
        step: 'Meditations written for your moment',
        text: 'Describe what you\'re going through, choose a focus, a length and a language — HYPSEYA writes a meditation and speaks it back at a genuinely meditative pace, with real silence between the words.',
      },
      {
        step: 'Why your own voice',
        text: 'The nervous system trusts no voice more than its own. Your voice, calm, describing safety — that isn\'t a gimmick. It\'s the most direct route to the system that needs to hear it.',
      },
    ],
    footer: 'A 30-second sample before every full meditation — check the tone before you commit.',
  },
  {
    id: 'eri',
    title: 'Your ERI',
    subtitle: 'Emotional Regulation Index',
    Visual: ERIVisual,
    cta: { label: 'See your ERI →', path: '/insights' },
    content: [
      {
        step: 'Regulation Capacity',
        text: 'Your nervous system\'s ability to shift between states intentionally. Not the absence of strong emotion — the capacity to move through it without getting stuck. Measured from your session check-ins over time.',
      },
      {
        step: 'Stability Score',
        text: 'Consistency of baseline across sessions. A high stability score means your nervous system is less reactive to daily variance — not flat, but less turbulent. It improves slowly, and that\'s the point.',
      },
      {
        step: 'Practice Consistency',
        text: 'Regularity of engagement. The ERI rewards consistency over intensity. Three sessions per week, week after week, is worth more than daily sprints followed by absence. The nervous system learns through repetition.',
      },
    ],
    footer: 'Scores 0–100. Not a diagnosis — a mirror. Changes with practice.',
  },
  {
    id: 'journal',
    title: 'The Journal answers back',
    subtitle: 'A question in, a reflection out',
    Visual: JournalVisual,
    cta: { label: 'Open your Journal →', path: '/journal' },
    content: [
      {
        step: 'It asks first',
        text: 'The Journal opens with a question built from your actual day — the session you just measured, the program week you\'re in. Never a blank page.',
      },
      {
        step: 'It answers back',
        text: 'When you save an entry, one sentence is reflected back to you — an observation, never advice. A thread noticed across your entries, or the strongest thing in today\'s.',
      },
      {
        step: 'Speak instead',
        text: 'Too tired or too activated to type? Tap the mic and talk. Your words are transcribed for you to edit — the lowest-friction door into reflection, exactly when you need it most.',
      },
    ],
    footer: 'Private. Never shared with practitioners. Ever.',
  },
  {
    id: 'programs',
    title: 'Programs',
    subtitle: '8-week journeys that measure your change',
    Visual: ProgramVisual,
    cta: { label: 'Explore programs →', path: '/programs' },
    content: [
      {
        step: '8-week structured journeys',
        text: 'Grief, Anxiety, Burnout, Trauma Integration, and Mind Redesign. Each week has a theme, a focus, and practices that plug straight into your Daily Loop — your program frames each day\'s session.',
      },
      {
        step: 'Measured change',
        text: 'Enrolling captures your baseline — PHQ-9, GAD-7, and your ERI. Week 8 invites a re-test. The completion report shows presence (what you actually did) and change (what actually moved). Completion is measured in practice, not in time elapsed.',
      },
      {
        step: 'Tools alongside',
        text: 'The Somatic Map tracks where emotion lives in your body. Proof of Change shows your longitudinal arc for the weeks when progress feels invisible. The Integration Report extracts meaning from significant experiences before the window closes.',
      },
    ],
    footer: null,
  },
  {
    id: 'circles',
    title: 'Integration Circles',
    subtitle: 'Anonymous collective intelligence',
    Visual: null,
    cta: { label: 'Visit your Circle →', path: '/circles' },
    content: [
      {
        step: 'How it works',
        text: 'When you complete an integration reflection, an anonymised version of your key insight is added to your program\'s Circle. AI distils what is universally resonant — themes that appear across many people\'s experience, without being traceable to any one of them.',
      },
      {
        step: 'What you see',
        text: 'A short set of distilled statements — the collective signal from everyone on your program. Not advice. Not diagnosis. Just what the nervous system, across dozens of people, seems to be working through at this stage of the journey.',
      },
      {
        step: 'Why it matters',
        text: 'Isolation is a nervous system response to pain. Seeing that others in the same territory are moving through similar terrain is itself regulatory. You don\'t need to know who they are. You just need to know you\'re not alone in the pattern.',
      },
    ],
    footer: 'No identities, no stories — just what resonated.',
  },
];

const FAQS = [
  {
    q: 'Who is this app for?',
    a: 'Anyone who wants to understand and regulate their emotional nervous system. Not a replacement for therapy — a complement to it. Works for people in therapy, people who\'ve tried therapy, and people who want to start before they\'re ready for therapy.',
  },
  {
    q: 'Is this a replacement for therapy?',
    a: 'No. HYPSEYA is a regulation and self-awareness tool. If you\'re in crisis or need clinical support, please reach out to a professional. The app has a crisis detection layer and will always point you to help when needed.',
  },
  {
    q: 'How is my data protected?',
    a: 'Your data is stored securely, never sold, and compliant with EU GDPR and health data regulations. All emotional data is yours — you can request deletion at any time. Journal entries are never shared with practitioners.',
  },
  {
    q: 'How is HYPSEYA different from other apps?',
    a: 'Most apps track mood. HYPSEYA measures regulation — and learns which practices move your nervous system specifically. Add meditations in your own voice and programs that measure your before-and-after, and the app becomes something no generic library can be: yours.',
  },
  {
    q: 'How long until I see results?',
    a: 'The ERI becomes meaningful after 3 sessions. Your practice ranking personalises within 2 weeks. Significant shifts typically appear around week 4–6 of consistent practice.',
  },
];

export default function Guide() {
  const navigate = useNavigate();
  const [openChapter, setOpenChapter] = useState('daily-loop');

  const toggle = (id) => setOpenChapter(prev => prev === id ? null : id);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #F7F3EC 0%, #EFE7DD 55%, rgba(184,211,207,0.15) 100%)',
      paddingBottom: '100px',
    }}>
      {/* Back button */}
      <div style={{ padding: '20px 20px 0', maxWidth: '600px', margin: '0 auto' }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: '6px',
            color: TEAL, fontSize: '0.85rem', fontWeight: '500',
            padding: '8px 0', letterSpacing: '0.02em',
          }}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M11 4L6 9l5 5" stroke={TEAL} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back
        </button>
      </div>

      {/* Header */}
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '24px 20px 28px' }}>
        <p style={{
          margin: '0 0 8px',
          fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.12em',
          color: '#8FA3A0', textTransform: 'uppercase',
        }}>
          Guide
        </p>
        <h1 style={{
          margin: '0 0 10px',
          fontFamily: "'Playfair Display', serif",
          fontSize: '1.9rem', fontWeight: '400',
          color: '#1E3A38', lineHeight: 1.2,
        }}>
          How HYPSEYA works
        </h1>
        <p style={{ margin: 0, fontSize: '0.92rem', color: '#7A9E9B', lineHeight: 1.6 }}>
          From your first session to measurable change.
        </p>
      </div>

      {/* ── Your first day — three doors, not a manual ── */}
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '0 20px 32px' }}>
        <p style={{
          margin: '0 0 12px', fontSize: '0.68rem', fontWeight: '700',
          letterSpacing: '0.12em', color: TEAL, textTransform: 'uppercase',
        }}>
          Your first day
        </p>
        {FIRST_DAY.map(item => (
          <button
            key={item.n}
            onClick={() => navigate(item.path)}
            style={{
              width: '100%', textAlign: 'left', cursor: 'pointer',
              background: '#fff', border: 'none', borderRadius: '16px',
              padding: '16px 18px', marginBottom: '8px',
              display: 'flex', alignItems: 'center', gap: '14px',
              boxShadow: '0 2px 12px rgba(30,107,104,0.07)',
            }}
          >
            <div style={{
              width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
              background: `linear-gradient(135deg, ${TEAL}, ${TEAL_LIGHT})`,
              color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.85rem', fontWeight: 700,
            }}>{item.n}</div>
            <div style={{ flex: 1 }}>
              <p style={{ margin: '0 0 2px', fontSize: '0.92rem', fontWeight: 600, color: '#1E3A38' }}>
                {item.title}
              </p>
              <p style={{ margin: 0, fontSize: '0.78rem', color: '#8FA3A0', lineHeight: 1.5 }}>
                {item.text}
              </p>
            </div>
            <span style={{ color: '#B8D3CF', fontSize: '1rem', flexShrink: 0 }}>→</span>
          </button>
        ))}
      </div>

      {/* ── Chapters ── */}
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '0 20px' }}>
        <p style={{
          margin: '0 0 12px', fontSize: '0.68rem', fontWeight: '700',
          letterSpacing: '0.12em', color: '#8FA3A0', textTransform: 'uppercase',
        }}>
          How it all fits together
        </p>
        {CHAPTERS.map((chapter, idx) => {
          const isOpen = openChapter === chapter.id;
          const Visual = chapter.Visual;
          return (
            <div
              key={chapter.id}
              style={{
                background: '#fff',
                borderRadius: '18px',
                marginBottom: '10px',
                boxShadow: '0 2px 12px rgba(30,107,104,0.06)',
                overflow: 'hidden',
                borderLeft: isOpen ? `3px solid ${TEAL}` : '3px solid transparent',
                transition: 'border-left-color 0.2s ease',
              }}
            >
              {/* Chapter header */}
              <button
                onClick={() => toggle(chapter.id)}
                style={{
                  width: '100%', background: 'none', border: 'none',
                  padding: '20px 20px',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  cursor: 'pointer', textAlign: 'left', gap: '12px',
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                    <span style={{
                      fontSize: '0.62rem', fontWeight: '700', letterSpacing: '0.1em',
                      color: '#B8D3CF', textTransform: 'uppercase',
                    }}>
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                    <h2 style={{
                      margin: 0,
                      fontFamily: "'Playfair Display', serif",
                      fontSize: '1.1rem', fontWeight: '600',
                      color: '#1E3A38',
                    }}>
                      {chapter.title}
                    </h2>
                  </div>
                  <p style={{
                    margin: 0, fontSize: '0.78rem', color: '#8FA3A0',
                    letterSpacing: '0.02em',
                  }}>
                    {chapter.subtitle}
                  </p>
                </div>
                <svg
                  width="16" height="16" viewBox="0 0 16 16" fill="none"
                  style={{
                    transform: isOpen ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.25s ease', flexShrink: 0,
                  }}
                >
                  <path d="M4 6l4 4 4-4" stroke="#8FA3A0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

              {/* Chapter body */}
              {isOpen && (
                <div style={{ padding: '0 20px 20px' }}>
                  {Visual && <Visual />}
                  {chapter.content.map(item => (
                    <div key={item.step} style={{ marginBottom: '16px' }}>
                      <p style={{
                        margin: '0 0 4px', fontSize: '0.8rem', fontWeight: '700',
                        color: TEAL, letterSpacing: '0.02em',
                      }}>
                        {item.step}
                      </p>
                      <p style={{ margin: 0, fontSize: '0.88rem', color: '#5A7A77', lineHeight: 1.65 }}>
                        {item.text}
                      </p>
                    </div>
                  ))}
                  {chapter.footer && (
                    <p style={{
                      margin: '4px 0 14px', fontSize: '0.78rem', color: '#9AADAB',
                      fontStyle: 'italic', lineHeight: 1.6,
                    }}>
                      {chapter.footer}
                    </p>
                  )}
                  <button
                    onClick={() => navigate(chapter.cta.path)}
                    style={{
                      padding: '10px 20px',
                      background: 'rgba(30,107,104,0.08)',
                      color: TEAL, border: `1.5px solid rgba(30,107,104,0.3)`,
                      borderRadius: '18px', fontSize: '0.83rem', fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >
                    {chapter.cta.label}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* FAQ section */}
      <div style={{ maxWidth: '600px', margin: '36px auto 0', padding: '0 20px' }}>
        <p style={{
          margin: '0 0 14px', fontSize: '0.68rem', fontWeight: '700',
          letterSpacing: '0.12em', color: '#8FA3A0', textTransform: 'uppercase',
        }}>
          Common questions
        </p>
        {FAQS.map(faq => (
          <details
            key={faq.q}
            style={{
              background: 'rgba(255,255,255,0.7)',
              borderRadius: '14px', marginBottom: '8px',
              padding: '14px 18px',
            }}
          >
            <summary style={{
              cursor: 'pointer', fontSize: '0.88rem', fontWeight: '600',
              color: '#1E3A38', listStyle: 'none',
            }}>
              {faq.q}
            </summary>
            <p style={{ margin: '10px 0 0', fontSize: '0.84rem', color: '#5A7A77', lineHeight: 1.65 }}>
              {faq.a}
            </p>
          </details>
        ))}
      </div>
    </div>
  );
}
